import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, MessageCircle, MapPin, Check, Clock } from "lucide-react";
import { COMPANY, SERVICES, BAIRROS, bairroUrl, serviceBairroUrl, waLink, bairrosImage } from "@/data/site";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { FAQAccordion } from "@/components/FAQAccordion";
import { CTABanner } from "@/components/CTABanner";

export default function BairroDetail() {
  const { slug } = useParams();
  const cleanSlug = slug?.replace(/\.html$/, "").replace(/^desentupidora-/, "");
  const bairro = BAIRROS.find((b) => b.slug === cleanSlug);
  if (!bairro) return <Navigate to="/bairros.html" replace />;

  const faq = [
    { q: `Vocês atendem em ${bairro.name}?`, a: `Sim! A Desentupidora Paulistana atende todo o bairro ${bairro.name} (${bairro.region}) e arredores, 24 horas por dia, incluindo finais de semana e feriados.` },
    { q: `Quanto tempo para chegar em ${bairro.name}?`, a: `Mantemos equipes próximas à região dos ${bairro.region}, com tempo médio de chegada de 15 a 30 minutos em ${bairro.name}.` },
    { q: `Quais serviços estão disponíveis em ${bairro.name}?`, a: `Todos: desentupimento de pia, vaso sanitário, esgoto, ralos, hidrojateamento e limpeza de caixa de gordura.` },
  ];

  return (
    <>
      <SEO
        title={`Desentupidora 24h em ${bairro.name} | Paulistana`}
        description={`Desentupidora 24 horas em ${bairro.name}, São Paulo. Desentupimento de pia, esgoto, vaso e hidrojateamento com chegada em até 15 min. Ligue ${COMPANY.phoneDisplay}.`}
        path={`/bairros/${bairro.slug}`}
      />
      <section className="bg-brand-black text-white">
        <div className="max-w-7xl mx-auto container-px py-12 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Bairros", to: "/bairros.html" }, { label: bairro.name }]} />
            <span className="inline-flex items-center gap-2 bg-wa-green text-white text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full mt-4 mb-3"><Clock className="w-4 h-4" /> Atendimento 24h</span>
            <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mb-4">Desentupidora 24h em {bairro.name}</h1>
            <p className="text-gray-300 mb-2 flex items-center gap-2"><MapPin className="w-4 h-4 text-brand-red" /> {bairro.name} • {bairro.region} • CEP {bairro.cep}</p>
            <p className="text-gray-300 mb-7">Emergência com cano entupido em {bairro.name}? Nossa equipe chega rápido, sem quebra-quebra e com garantia. Atendemos residências, condomínios e empresas.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href={`tel:${COMPANY.phoneTel}`} className="btn-red"><Phone className="w-5 h-5" /> {COMPANY.phoneDisplay}</a>
              <a href={waLink(`Olá! Preciso de desentupidora em ${bairro.name}.`)} target="_blank" rel="noopener noreferrer" className="btn-green"><MessageCircle className="w-5 h-5" /> Chamar no WhatsApp</a>
            </div>
          </div>
          <img src={bairrosImage} alt={`Desentupidora atendendo em ${bairro.name}`} className="rounded-xl w-full object-cover shadow-lg" />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto container-px py-16">
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-brand-black mb-2">Serviços disponíveis em {bairro.name}</h2>
          <p className="text-gray-600 mb-8">Soluções completas de desentupimento para a região dos {bairro.region}.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map((s) => (
              <Link key={s.slug} to={serviceBairroUrl(s.slug, bairro.slug)} className="group bg-brand-light rounded-lg p-5 hover:shadow-md transition-all border border-transparent hover:border-brand-red">
                <span className="inline-block bg-brand-red text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded mb-2">{s.label}</span>
                <h3 className="font-display font-bold text-base text-brand-black group-hover:text-brand-red transition-colors">{s.name} em {bairro.name}</h3>
                <p className="text-sm text-gray-600 mt-1.5 line-clamp-2">{s.short}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-light border-y border-gray-200">
        <div className="max-w-7xl mx-auto container-px py-14 grid lg:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display font-extrabold text-2xl text-brand-black mb-5">Por que somos referência em {bairro.name}</h2>
            <ul className="space-y-3">
              {[`Equipe próxima à região dos ${bairro.region}`, "Chegada em até 15 minutos", "Sem quebra de pisos ou azulejos", "Orçamento transparente e garantia"].map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="grid place-items-center w-6 h-6 rounded-full bg-wa-green shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-white" /></span>
                  <span className="text-gray-700">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-display font-extrabold text-2xl text-brand-black mb-5">Dúvidas sobre {bairro.name}</h2>
            <FAQAccordion items={faq} testIdPrefix="bairro-faq" />
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto container-px py-14">
          <h2 className="font-display font-extrabold text-xl text-brand-black mb-5">Outros bairros que atendemos</h2>
          <div className="flex flex-wrap gap-2.5">
            {BAIRROS.filter((b) => b.slug !== bairro.slug).slice(0, 12).map((b) => (
              <Link key={b.slug} to={bairroUrl(b.slug)} className="text-sm bg-brand-light hover:bg-brand-red hover:text-white transition-colors rounded-full px-4 py-2 text-brand-black">{b.name}</Link>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
